import { Button } from '@mui/material';
import { t } from 'i18next';
import { Outlet } from 'react-router-dom';
import { BaseController, BaseState } from '../../../common/controllers/BaseController';
import { AuthorizationError } from '../../../common/errors/errors';
import { withRouter } from '../../../common/higher-order-components/with-router';
import { UrlHelper } from '../../../common/utils/UrlHelper';
import { bind } from '../../../common/utils/decorators';
import { SecondaryNav } from '../../../layout/components/SecondaryNav';
import { ModalManager } from '../../../modal-flows/services/ModalManager';
import { PATHS, Route, SCANS_TABS, TabNameLocalizationKeys } from '../../../routing/routes';
import { ScansContextType } from '../contexts/ScansContext';

type Props = {};

type State = BaseState & {
  nonce: number;
  canCreateScan: boolean;
  canStartCrowdStrikeScan: boolean;
};

class Controller extends BaseController<Props, State> {
  private readonly modalManager = new ModalManager();

  constructor(props: Props, context: unknown) {
    super(props, context);

    this.state = {
      ...super.getBaseState(),
      nonce: 0,
      canCreateScan: false,
      canStartCrowdStrikeScan: false,
    };
  }

  protected shouldCheckPermissionToAccess(): boolean {
    return true;
  }

  protected async checkPermissionToAccess(): Promise<void> {
    const permission = await this.permissionsService.canAccessOrganizationScans();
    if (!permission) {
      throw new AuthorizationError(PATHS.ORGANIZATION_DASHBOARD);
    }
    this.loadPermissions();
  }

  getNestedTabs(): Route[] {
    return UrlHelper.generatePaths(SCANS_TABS(), {});
  }

  @bind
  private getBreadcrumbs() {
    return this.breadcrumbFactory.getScansBreadcrumbs();
  }

  render() {
    const { nonce, canCreateScan, canStartCrowdStrikeScan } = this.state;
    return this.renderBase(
      <SecondaryNav
        title={t(TabNameLocalizationKeys.SCANS)}
        subtitle={t('scansListSubtitle')}
        tabs={this.getNestedTabs()}
        getBreadcrumbs={this.getBreadcrumbs}
        loading={false}
        rightSection={
          canCreateScan && canStartCrowdStrikeScan ? (
            <Button variant="contained" onClick={this.handleCreateScan}>
              {t('startScan')}
            </Button>
          ) : null
        }
      >
        <Outlet context={{ nonce } satisfies ScansContextType} />
      </SecondaryNav>
    );
  }

  @bind
  private async handleCreateScan() {
    const { canStartCrowdStrikeScan } = this.state;
    if (!canStartCrowdStrikeScan) return;

    const { scanId } = await this.modalManager.createScan();

    if (!scanId) return;

    // Trigger a refresh of the nested scans table
    this.setState((prev) => ({ ...prev, nonce: prev.nonce + 1 }));
  }

  private async loadPermissions() {
    try {
      const [canCreateScan, canStartCrowdStrikeScan] = await Promise.all([
        this.permissionsService.canCreateScan(),
        this.permissionsService.canStartCrowdStrikeScan(),
      ]);
      this.setState({ canCreateScan, canStartCrowdStrikeScan });
    } catch (e: unknown) {
      this.handleError(e);
    }
  }
}

export const ScansController = withRouter(Controller);
